import { useState, useMemo, useCallback, useRef } from "react";
import {
  UniversalTable,
  LazyLoadTrigger,
  TableColumnsConfig,
} from "shared/components/UniversalTable";
import { useBuildingFinancialsData } from "./hooks/useBuildingFinancialsData";
import {
  BUILDING_FINANCIALS_COLUMNS,
  BUILDING_FINANCIALS_FILTER_SECTIONS,
  BUILDING_FINANCIALS_GROUPBY_OPTIONS,
  extractCompanyOptions,
} from "./config/buildingFinancialsTableConfig";
import {
  createBuildingFinancialsCells,
  clearCellCache,
} from "./config/buildingFinancialsCellFactory";
import { BUILDING_FINANCIALS_PICKER_CONFIG } from "./config/buildingFinancialsPickerConfig";
import EllaAvatar from "assets/ella.png";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { TableSortConfig } from "shared/components/UniversalTable/types/SortConfig";
import { Button } from "components/ui/button";
import {
  ArrowClockwise,
  CircleNotch,
  Lock,
  PencilSimple,
} from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { SyncStatusIndicator } from "./components/SyncStatusIndicator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { useSyncStatus } from "./hooks/useSyncStatus";

export const BuildingFinancialsPage = () => {
  const { session } = useSession();
  const { syncStatus, refetch: refetchSyncStatus } = useSyncStatus();

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [companyFilters, setCompanyFilters] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string>("none");
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Use ref for selectedRows to prevent renderRow re-creation on every selection change
  // This is the CRITICAL optimization that eliminates 1,350 cell renders per checkbox click
  const selectedRowsRef = useRef<Set<number>>(selectedRows);
  selectedRowsRef.current = selectedRows;

  // Picker modal state
  const [pickerState, setPickerState] = useState<{
    isOpen: boolean;
    mode: string | null;
    currentValue: any;
    buildingIds: string[];
  }>({
    isOpen: false,
    mode: null,
    currentValue: null,
    buildingIds: [],
  });

  // Sync loading state
  const [isSyncing, setIsSyncing] = useState(false);

  // Load all loading state
  const [isLoadingAll, setIsLoadingAll] = useState(false);

  // Changes modal state
  const [changesModalOpen, setChangesModalOpen] = useState(false);
  const [financialChanges, setFinancialChanges] = useState<any[]>([]);

  // Fetch data with server-side filtering
  const {
    buildingFinancials,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    observerTarget,
    setBuildingFinancials,
    refreshBuildingFinancials,
    loadMoreManually,
  } = useBuildingFinancialsData({
    searchText,
    companyIds: companyFilters,
  });

  // Ref to track current buildings length for load all functionality
  // MUST be defined AFTER useBuildingFinancialsData to avoid uninitialized variable error
  const buildingFinancialsRef = useRef(buildingFinancials);
  buildingFinancialsRef.current = buildingFinancials;

  // Handle load all buildings
  const handleLoadAll = useCallback(async () => {
    if (isLoadingAll || isLoadingMore) return;

    setIsLoadingAll(true);

    try {
      // Keep loading more until all buildings are loaded
      let attempts = 0;
      const maxAttempts = 100; // Safety limit to prevent infinite loops
      let previousLength = buildingFinancialsRef.current.length;

      console.log(
        `Starting load all: current=${previousLength}, total=${totalCount}`,
      );

      while (
        buildingFinancialsRef.current.length < (totalCount as number) &&
        attempts < maxAttempts
      ) {
        console.log(
          `Attempt ${attempts + 1}: Loading more (current: ${buildingFinancialsRef.current.length}/${totalCount})`,
        );

        await loadMoreManually();
        attempts++;

        // Small delay to allow state to update
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Check if we actually loaded more data (prevent infinite loop if API stops returning data)
        const currentLength = buildingFinancialsRef.current.length;
        console.log(
          `After load: length changed from ${previousLength} to ${currentLength}`,
        );

        if (currentLength === previousLength) {
          console.warn(
            `No new buildings loaded on attempt ${attempts}, stopping.`,
          );
          break;
        }

        if (currentLength >= (totalCount as number)) {
          console.log(`All buildings loaded: ${currentLength}/${totalCount}`);
          break;
        }

        previousLength = currentLength;
      }

      toast.success(
        `Alle ${buildingFinancialsRef.current.length} gebouwen geladen`,
      );
    } catch (error) {
      console.error("Error loading all buildings:", error);
      toast.error("Fout bij laden van alle gebouwen");
    } finally {
      setIsLoadingAll(false);
    }
  }, [isLoadingAll, isLoadingMore, totalCount, loadMoreManually]);

  // Handle cache clearing and refetch
  const handleClearCache = useCallback(async () => {
    if (!session || isSyncing) return;

    setIsSyncing(true);

    try {
      // Store current sync timestamp before clearing
      const previousSyncTime = syncStatus?.lastSyncCompleted;

      // Capture current financial data before sync
      const oldFinancials = new Map(
        buildingFinancials.map((b) => [b.id, { ...b }]),
      );

      const response = await fetchWithAuth(
        `${API_URL}/building-financials/clear-cache`,
        { method: "POST" },
        session,
      );

      if (response.ok) {
        // Clear cell factory cache
        clearCellCache();

        // Poll for sync completion (check every 3 seconds, max 20 attempts = 60 seconds)
        let attempts = 0;
        const maxAttempts = 20;
        const pollInterval = 3000;

        const pollForCompletion = setInterval(async () => {
          attempts++;

          // Get the latest sync status after refetch
          const latestResponse = await fetchWithAuth(
            `${API_URL}/building-financials/sync-status`,
            { method: "GET" },
            session,
          );

          if (latestResponse.ok) {
            const latestStatus = await latestResponse.json();
            const newSyncTime = latestStatus.lastSyncCompleted;

            // Check if sync completed (timestamp changed)
            if (newSyncTime && newSyncTime !== previousSyncTime) {
              clearInterval(pollForCompletion);
              setIsSyncing(false);

              // Immediately update sync status in UI
              await refetchSyncStatus();

              // Refresh data and detect changes
              await refreshBuildingFinancials();

              // Wait a bit for data to load, then calculate changes
              setTimeout(() => {
                const changes: any[] = [];
                const financialFields = [
                  "renovationCostsToDate",
                  "totalNetRentalIncome",
                  "totalRentalIncome",
                  "propertyCosts",
                  "totalBid",
                  "transactionCosts",
                  "brokerCostsNet",
                  "estimatedMarketValue",
                ];

                buildingFinancials.forEach((newBuilding) => {
                  const oldBuilding = oldFinancials.get(newBuilding.id);
                  if (!oldBuilding) return;

                  const buildingChanges: any = {
                    buildingId: newBuilding.id,
                    address: newBuilding.googlePlaceLongAddress,
                    changes: [],
                  };

                  financialFields.forEach((field) => {
                    const oldValue = oldBuilding[field];
                    const newValue = newBuilding[field];

                    // Only track if both values exist and are different
                    if (
                      oldValue != null &&
                      newValue != null &&
                      oldValue !== newValue
                    ) {
                      buildingChanges.changes.push({
                        field,
                        oldValue,
                        newValue,
                      });
                    }
                  });

                  if (buildingChanges.changes.length > 0) {
                    changes.push(buildingChanges);
                  }
                });

                // Store changes for viewing later
                setFinancialChanges(changes);

                // Show success toast
                if (changes.length > 0) {
                  toast.success(
                    `Synchronisatie voltooid! ${changes.length} gebouw(en) met wijzigingen.`,
                  );
                } else {
                  toast.success(
                    `Synchronisatie voltooid! Geen wijzigingen gedetecteerd.`,
                  );
                }
              }, 1000);

              return;
            }
          }

          // Also refetch sync status during polling to update UI progressively
          await refetchSyncStatus();

          // Check if we've exceeded max attempts
          if (attempts >= maxAttempts) {
            clearInterval(pollForCompletion);
            setIsSyncing(false);
            await refetchSyncStatus();
            toast.warning(
              "Synchronisatie neemt langer dan verwacht. De pagina wordt automatisch bijgewerkt.",
            );
            await refreshBuildingFinancials();
          }
        }, pollInterval);
      } else {
        throw new Error("Failed to clear cache");
      }
    } catch (error) {
      console.error("Error clearing cache:", error);
      setIsSyncing(false);
      toast.error("Fout bij wissen van cache");
    }
  }, [
    session,
    isSyncing,
    buildingFinancials,
    refreshBuildingFinancials,
    refetchSyncStatus,
    syncStatus,
  ]);

  // Handle picker event from cell clicks
  const handlePickerEvent = useCallback(
    (event: { mode: string; buildingIds: string[]; currentValue: any }) => {
      console.log("📝 Picker event:", event);
      setPickerState({
        isOpen: true,
        mode: event.mode,
        currentValue: event.currentValue,
        buildingIds: event.buildingIds,
      });
    },
    [],
  );

  // Handle picker close
  const handlePickerClose = useCallback(() => {
    setPickerState({
      isOpen: false,
      mode: null,
      currentValue: null,
      buildingIds: [],
    });
  }, []);

  // Handle picker confirmation (update the field)
  const handlePickerConfirm = useCallback(
    async (value: any) => {
      if (!session || pickerState.buildingIds.length === 0 || !pickerState.mode)
        return;

      const buildingIds = pickerState.buildingIds;
      const fieldMapping = BUILDING_FINANCIALS_PICKER_CONFIG.fieldMappings.find(
        (mapping) => mapping.eventMode === pickerState.mode,
      );

      if (!fieldMapping) {
        toast.error("Onbekend veld");
        return;
      }

      const fieldLabel =
        BUILDING_FINANCIALS_PICKER_CONFIG.fieldLabels?.[
          fieldMapping.fieldName
        ] || fieldMapping.fieldName;
      const payload = fieldMapping.buildUpdatePayload?.(value);

      console.log(
        `🔄 Updating ${buildingIds.length} building(s):`,
        buildingIds,
      );
      console.log("Field:", fieldMapping.fieldName, "Value:", value);

      try {
        // Call API for each building
        const updatePromises = buildingIds.map(async (buildingId) => {
          const endpoint =
            BUILDING_FINANCIALS_PICKER_CONFIG?.apiUpdateEndpoint?.replace(
              "{id}",
              buildingId,
            );
          const response = await fetchWithAuth(
            `${API_URL}${endpoint}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            },
            session,
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              `Fout bij ${buildingId}: ${errorData.message || "Onbekende fout"}`,
            );
          }

          return buildingId;
        });

        // Wait for all updates to complete
        const updatedIds = await Promise.all(updatePromises);

        // Clear cell factory cache to ensure fresh render
        clearCellCache();

        // Soft update: Update local state instead of full refresh
        console.log(
          "✅ All updates successful, performing soft update in local state",
        );
        setBuildingFinancials((prevFinancials) =>
          prevFinancials.map((building) => {
            if (updatedIds.includes(building.id)) {
              console.log(
                `  📝 Updating building ${building.id} field ${fieldMapping.fieldName} to`,
                value,
              );
              // Update the field in the local state
              const updatedBuilding = {
                ...building,
                [fieldMapping.fieldName]: value,
              };
              console.log(`  ✓ Updated building object:`, updatedBuilding);
              return updatedBuilding;
            }
            return building;
          }),
        );

        // Show success toast
        if (buildingIds.length === 1) {
          toast.success(
            `${fieldLabel} bijgewerkt voor gebouw ${buildingIds[0]}`,
          );
        } else {
          toast.success(
            `${fieldLabel} bijgewerkt voor ${buildingIds.length} gebouwen`,
          );
        }

        // Clear selection after bulk update
        if (buildingIds.length > 1) {
          setSelectedRows(new Set());
        }

        // Close the modal
        handlePickerClose();
      } catch (error) {
        console.error("Error updating building financial field:", error);
        toast.error(
          error instanceof Error
            ? error.message
            : "Fout bij bijwerken van veld",
        );
      }
    },
    [session, pickerState, setBuildingFinancials, handlePickerClose],
  );

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(searchText || (companyFilters && companyFilters.length > 0));
  }, [searchText, companyFilters]);

  // Dynamically extract company options from buildings
  const filterSectionsWithData = useMemo(() => {
    const companyOptions = extractCompanyOptions(buildingFinancials);

    return BUILDING_FINANCIALS_FILTER_SECTIONS.map((section) => {
      if (section.key === "company") {
        return { ...section, options: companyOptions };
      }
      return section;
    });
  }, [buildingFinancials]);

  // Handle filter changes
  const handleFilterChange = useCallback((key: string, values: string[]) => {
    if (key === "company") {
      setCompanyFilters(values);
    }
  }, []);

  // Render row using cell factory
  // CRITICAL OPTIMIZATION: Use selectedRowsRef.current instead of selectedRows
  // This prevents renderRow from being recreated on every selection change
  // Result: Only re-renders changed rows (27 cells) instead of entire table (1,350 cells)
  const renderRow = useCallback(
    (building: any, context: any) => {
      // UniversalTable provides isSelected and onToggleSelect in context
      // Add onPickerEvent handler and selectedRows to context
      const enhancedContext = {
        ...context,
        onPickerEvent: handlePickerEvent,
        selectedRows: selectedRowsRef.current, // Use ref to avoid dependency on selectedRows state
      };
      const cells = createBuildingFinancialsCells(building, enhancedContext);
      return cells;
    },
    [handlePickerEvent],
  ); // Removed selectedRows from dependencies - this is the key optimization!

  // Custom Ella loading state for initial load
  const ellaInitialLoadingState = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <img
          src={EllaAvatar}
          alt="Ella"
          className="h-16 w-16 animate-pulse rounded-full"
        />
        <p className="text-neutral-primary text-base font-medium">
          Ella haalt financiële gegevens op...
        </p>
        <p className="text-neutral-secondary-mid text-sm">Dit kan even duren</p>
      </div>
    ),
    [],
  );

  // Custom Ella loading state for filtering
  const ellaFilteringState = useMemo(
    () => (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <img
          src={EllaAvatar}
          alt="Ella"
          className="h-16 w-16 animate-pulse rounded-full"
        />
        <p className="text-neutral-primary text-base font-medium">
          Ella filtert de financiële gegevens...
        </p>
        <p className="text-neutral-secondary-mid text-sm">Een moment geduld</p>
      </div>
    ),
    [],
  );

  // Determine which loading state to show based on context
  const currentLoadingState = useMemo(() => {
    if (hasActiveFilters) {
      return ellaFilteringState;
    }
    return ellaInitialLoadingState;
  }, [hasActiveFilters, ellaFilteringState, ellaInitialLoadingState]);

  // Format last sync timestamp for tooltip
  const formattedLastSync = useMemo(() => {
    if (!syncStatus?.lastSyncCompleted) return null;

    const date = new Date(syncStatus.lastSyncCompleted);
    return date.toLocaleString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/Brussels",
    });
  }, [syncStatus?.lastSyncCompleted]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Financials Gebouwen &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>
      <div className="bg-brick-secondary-fine flex flex-col gap-0 pb-32">
        {/* Legend */}
        <div className="border-neutral-stroke border-b bg-white px-6 py-3">
          <div className="flex items-center gap-6">
            <span className="text-neutral-primary text-sm font-medium">
              Legenda:
            </span>
            <div className="flex items-center gap-2">
              <div className="bg-moss-secondary-light border-moss-primary/20 flex items-center gap-2 rounded border px-3 py-1.5">
                <PencilSimple
                  size={16}
                  weight="fill"
                  className="text-moss-primary"
                />
                <span className="text-neutral-primary text-sm">Bewerkbaar</span>
              </div>
              <div className="bg-neutral-secondary-light border-neutral-stroke flex items-center gap-2 rounded border px-3 py-1.5">
                <Lock
                  size={16}
                  weight="fill"
                  className="text-neutral-secondary-mid"
                />
                <span className="text-neutral-primary text-sm">
                  Vergrendeld (berekend/gesynct)
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Header with actions */}
        <div className="border-neutral-stroke flex items-center justify-between border-b bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            {/* Sync Status Indicator */}
            <SyncStatusIndicator
              onViewChanges={() => setChangesModalOpen(true)}
            />

            {/* Load All Button */}
            <Button
              onClick={handleLoadAll}
              variant="outline"
              size="sm"
              disabled={isLoading || isLoadingAll || !hasMore}
            >
              {isLoadingAll ? (
                <CircleNotch size={16} className="mr-2 animate-spin" />
              ) : (
                <ArrowClockwise size={16} className="mr-2" />
              )}
              {isLoadingAll
                ? `Laden... (${buildingFinancials.length}/${totalCount})`
                : hasMore
                  ? `Laad alle gebouwen (${totalCount})`
                  : `Alle gebouwen geladen (${totalCount})`}
            </Button>

            {/* Sync Button with Tooltip */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={handleClearCache}
                    variant="outline"
                    size="sm"
                    disabled={isLoading || isSyncing}
                  >
                    {isSyncing ? (
                      <CircleNotch size={16} className="mr-2 animate-spin" />
                    ) : (
                      <ArrowClockwise size={16} className="mr-2" />
                    )}
                    {isSyncing
                      ? "Synchroniseren..."
                      : "Cache wissen & ophalen van boekhouding"}
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  side="bottom"
                  className="border-neutral-stroke max-w-sm border bg-white p-4 shadow-lg"
                >
                  <div className="space-y-2">
                    <div className="text-sm font-semibold text-gray-900">
                      Automatische sync
                    </div>
                    <div className="border-neutral-stroke border-t"></div>
                    <p className="text-xs leading-relaxed text-gray-700">
                      Deze data wordt elke nacht automatisch gesynchroniseerd om{" "}
                      <strong className="text-gray-900">
                        00:30 (Brussel tijd)
                      </strong>
                      .
                    </p>
                    {formattedLastSync && (
                      <p className="text-xs text-gray-700">
                        <strong className="text-gray-900">Huidige data:</strong>{" "}
                        {formattedLastSync}
                      </p>
                    )}
                    <p className="text-xs leading-relaxed text-yellow-700">
                      Het is niet aan te raden om handmatig te synchroniseren,
                      tenzij u dringende updates nodig heeft. Wacht bij voorkeur
                      op de automatische nachtelijke sync.
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <UniversalTable
          columnsConfig={
            {
              columns: BUILDING_FINANCIALS_COLUMNS,
              sortConfig: {
                defaultSortKey: "id",
                defaultDirection: "asc",
              } as TableSortConfig,
            } as TableColumnsConfig
          }
          tableId="building-financials-v1"
          rows={buildingFinancials}
          title="Financials Gebouwen"
          description="Overzicht van alle financiële gegevens per gebouw"
          groupBy={groupBy}
          isLoading={isLoading}
          isLoadingMore={isLoadingMore}
          customLoadingState={currentLoadingState}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          sortConfig={{
            fields: BUILDING_FINANCIALS_COLUMNS.map((column) => ({
              key: column.id,
              extractor: (row: any) => row[column.id],
            })),
            defaultSortKey: "id",
            defaultDirection: "asc",
          }}
          searchConfig={{
            searchPlaceholder: "Zoeken op gebouw ID, adres of bedrijf...",
            filterSections: filterSectionsWithData,
            groupByOptions: BUILDING_FINANCIALS_GROUPBY_OPTIONS,
            onGroupByChange: setGroupBy,
            searchValue: searchText,
            onSearchChange: setSearchText,
            activeFilters: {
              company: companyFilters,
            },
            onFilterChange: handleFilterChange,
            serverSideFiltering: true,
            isLoadingMore: isLoadingMore,
            lazyLoadingContent: (
              <div className="flex items-center gap-2">
                <img
                  src={EllaAvatar}
                  alt="Ella"
                  className="h-6 w-6 animate-pulse rounded-full"
                />
                <span className="text-neutral-secondary text-sm">
                  Ella haalt meer gebouwen op...
                </span>
              </div>
            ),
          }}
          renderRow={renderRow}
          lazyLoadTrigger={
            <LazyLoadTrigger
              observerRef={observerTarget}
              hasMore={hasMore}
              isLoadingMore={isLoadingMore}
              allLoadedMessage="Alle gebouwen geladen"
              totalCount={buildingFinancials.length}
              totalAvailable={totalCount}
              onManualLoadMore={loadMoreManually}
            />
          }
          excelExportConfig={{
            filename: "gebouwen-financials",
            sheetName: "Financials",
            buttonText: "Naar Excel exporteren",
            excludeColumns: ["checkbox"],
            columnMapping: {
              id: "id",
              address: "googlePlaceLongAddress",
              company: "company.name",
              acquisitionDate: "acquisitionDate",
              totalBid: "totalBid",
              transactionCosts: "transactionCosts",
              brokerCostsNet: "brokerCostsNet",
              dueDiligenceCost: "dueDiligenceCost",
              fiscalDiscountCompanyShares: "fiscalDiscountCompanyShares",
              fiscalDiscountRealEstate: "fiscalDiscountRealEstate",
              otherDiscounts: "otherDiscounts",
              totalCashOutBeforeRenovation: "totalCashOutBeforeRenovation",
              deferredRegistrationTaxes: "deferredRegistrationTaxes",
              deferredRegistrationDate: "deferredRegistrationDate",
              renovationBudget: "renovationBudget",
              renovationCostsToDate: "renovationCostsToDate",
              estimatedMarketValue: "estimatedMarketValue",
              insuredValue: "insuredValue",
              contractualRentsPlusErv: "contractualRentsPlusErv",
              ervTotal: "ervTotal",
              ervResidentialUnits: "ervResidentialUnits",
              ervRetailUnits: "ervRetailUnits",
              ervOfficeUnits: "ervOfficeUnits",
              totalNetRentalIncome: "totalNetRentalIncome",
              totalRentalIncome: "totalRentalIncome",
              propertyCosts: "propertyCosts",
            },
            currencyColumns: [
              "totalBid",
              "transactionCosts",
              "brokerCostsNet",
              "dueDiligenceCost",
              "fiscalDiscountCompanyShares",
              "fiscalDiscountRealEstate",
              "otherDiscounts",
              "totalCashOutBeforeRenovation",
              "deferredRegistrationTaxes",
              "renovationBudget",
              "renovationCostsToDate",
              "estimatedMarketValue",
              "insuredValue",
              "contractualRentsPlusErv",
              "ervTotal",
              "ervResidentialUnits",
              "ervRetailUnits",
              "ervOfficeUnits",
              "totalNetRentalIncome",
              "totalRentalIncome",
              "propertyCosts",
            ],
            percentageColumns: ["percentageOperationalCosts"],
            freezeColumns: 2, // Freeze ID and Address columns (checkbox is excluded from export)
          }}
        />
      </div>

      {/* Picker Modal for Editable Fields */}
      {pickerState.isOpen &&
        pickerState.mode &&
        (() => {
          // Find the field mapping to get the picker mode and field label
          const fieldMapping =
            BUILDING_FINANCIALS_PICKER_CONFIG.fieldMappings.find(
              (mapping) => mapping.eventMode === pickerState.mode,
            );

          if (!fieldMapping) return null;

          const fieldLabel =
            BUILDING_FINANCIALS_PICKER_CONFIG.fieldLabels?.[
              fieldMapping.fieldName
            ] || fieldMapping.fieldName;
          const isBulk = pickerState.buildingIds.length > 1;

          return (
            <UniversalPickerModal
              isOpen={pickerState.isOpen}
              mode={fieldMapping.pickerMode as any}
              title={
                <div className="flex flex-col gap-2">
                  <span>{fieldLabel} instellen</span>
                  <div className="flex flex-wrap gap-1.5">
                    {pickerState.buildingIds.map((id) => (
                      <span
                        key={id}
                        className="bg-moss-secondary-mid text-moss-primary inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                      >
                        {id}
                      </span>
                    ))}
                  </div>
                  {isBulk && (
                    <span className="text-neutral-secondary-mid text-xs font-normal">
                      {pickerState.buildingIds.length} gebouwen geselecteerd
                    </span>
                  )}
                </div>
              }
              onClose={handlePickerClose}
              onConfirm={handlePickerConfirm}
              config={{
                defaultValue: pickerState.currentValue,
                // Custom placeholder for currency fields
                searchPlaceholder:
                  pickerState.mode === "insuredValue"
                    ? "Vul verzekerde waarde in"
                    : pickerState.mode === "estimatedMarketValue"
                      ? "Vul geschatte marktwaarde in"
                      : undefined,
                // Currency formatting for value fields
                apiConfig:
                  pickerState.mode === "insuredValue" ||
                  pickerState.mode === "estimatedMarketValue"
                    ? { format: "currency" }
                    : undefined,
              }}
            />
          );
        })()}

      {/* Sync Changes Modal */}
      {changesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 flex max-h-[80vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            {/* Header */}
            <div className="border-neutral-stroke border-b px-6 py-4">
              <h2 className="text-neutral-primary text-lg font-semibold">
                Synchronisatie Details
              </h2>
              <p className="text-neutral-secondary mt-1 text-sm">
                {financialChanges.length} gebouw(en) met wijzigingen
                gedetecteerd
              </p>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-6">
                {financialChanges.map((buildingChange) => (
                  <div
                    key={buildingChange.buildingId}
                    className="border-neutral-stroke rounded-lg border p-4"
                  >
                    {/* Building Header */}
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-neutral-primary font-semibold">
                        {buildingChange.buildingId}
                      </span>
                      <span className="text-neutral-secondary text-sm">
                        {buildingChange.address}
                      </span>
                      <span className="bg-moss-secondary-mid text-moss-primary ml-auto rounded px-2 py-1 text-xs">
                        {buildingChange.changes.length} wijziging(en)
                      </span>
                    </div>

                    {/* Changes Table */}
                    <div className="space-y-2">
                      {buildingChange.changes.map(
                        (change: any, idx: number) => {
                          const fieldLabels: Record<string, string> = {
                            renovationCostsToDate: "Renovatiekosten",
                            totalNetRentalIncome: "Netto Huurinkomsten",
                            totalRentalIncome: "Bruto Huurinkomsten",
                            propertyCosts: "Vastgoedkosten",
                            totalBid: "Aankoopprijs",
                            transactionCosts: "Transactiekosten",
                            brokerCostsNet: "Makelaarkosten",
                            estimatedMarketValue: "Geschatte Marktwaarde",
                          };

                          const formatValue = (val: any) => {
                            if (val == null) return "-";
                            if (typeof val === "number") {
                              return new Intl.NumberFormat("nl-BE", {
                                style: "currency",
                                currency: "EUR",
                                minimumFractionDigits: 2,
                              }).format(val);
                            }
                            return String(val);
                          };

                          return (
                            <div
                              key={idx}
                              className="border-neutral-stroke-light flex items-center gap-4 border-b py-2 last:border-0"
                            >
                              <div className="text-neutral-secondary w-48 text-sm font-medium">
                                {fieldLabels[change.field] || change.field}
                              </div>
                              <div className="flex flex-1 items-center gap-3">
                                <div className="text-neutral-secondary flex-1 text-sm line-through">
                                  {formatValue(change.oldValue)}
                                </div>
                                <div className="text-neutral-secondary-mid">
                                  →
                                </div>
                                <div className="text-moss-primary flex-1 text-sm font-semibold">
                                  {formatValue(change.newValue)}
                                </div>
                              </div>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-neutral-stroke flex justify-end border-t px-6 py-4">
              <Button
                onClick={() => setChangesModalOpen(false)}
                variant="outline"
              >
                Sluiten
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BuildingFinancialsPage;
